import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  EnumCashierTxStatus,
  EnumConfirmationStatus,
  type ResendEmailVerificationWithdrawalRequestData,
  type CashierTradingAccount,
  type CalculatedTransactions,
} from "@/api/cashier";
import { GridPageLayout } from "@/components/general/grid-page-layout/GridPageLayout";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { Section } from "@/components/general/section";
import { TransactionStatus } from "@/components/transaction/transaction-status/TransactionStatus";
import { ERoutes } from "@/utils/enums";
import {
  getWithdrawalRequestStatusOptions,
  resendEmailVerificationWithdrawalRequestMutation,
} from "@/api/cashier/@tanstack/react-query.gen";
import { useOtpVerifyWithdrawal } from "@/hooks/withdrawals/useOtpVerifyWithdrawal";
import { useWithdrawalOtpMfa } from "@/hooks/withdrawals/useWithdrawalOtpMfa";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useLogger } from "@/hooks/useLogger";

interface VerificationState {
  withdrawal_request_id: string;
  verification_type: string;
  created_at: string;
  time_limit_in_minutes: number;
  selectedAccount: CashierTradingAccount;
  transactionData: CalculatedTransactions;
}

export const WithdrawalVerification = () => {
  const { t } = useTranslation();
  const logger = useLogger();
  const navigate = useNavigate();
  const { id: withdrawalRequestId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const verificationQuery = queryParams.get("verification");

  const { selectedAccount } = useAccount();

  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const hasRequestedMfaRef = useRef(false);

  const verificationData: VerificationState | undefined = location.state;
  const isEmailVerification = verificationQuery === "email";
  const isOtpVerification = verificationQuery === "otp";

  const { data: statusData } = useQuery({
    ...getWithdrawalRequestStatusOptions({
      path: { withdrawalRequestID: withdrawalRequestId ?? "" },
    }),
    enabled: !!withdrawalRequestId,
    refetchInterval: 30_000,
  });

  const createdAt = statusData?.created_at ?? verificationData?.created_at;
  const timeLimitInMinutes =
    statusData?.time_limit_in_minutes ??
    verificationData?.time_limit_in_minutes;

  const RESEND_TIMEOUT_SECONDS = 60;
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(
    RESEND_TIMEOUT_SECONDS
  );
  const resendCooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate initial timer seconds based on elapsed time since created_at
  const initialTimerSeconds = (() => {
    if (!createdAt || !timeLimitInMinutes) return 60 * 60;
    const totalSeconds = timeLimitInMinutes * 60;
    const createdAtTime = new Date(createdAt).getTime();
    const currentTime = Date.now();
    const elapsedSeconds = Math.floor((currentTime - createdAtTime) / 1000);
    return Math.max(0, totalSeconds - elapsedSeconds);
  })();

  // Manage resend cooldown timer
  useEffect(() => {
    if (!isEmailVerification) {
      return () => {};
    }

    if (resendCooldownSeconds > 0) {
      resendCooldownIntervalRef.current = setInterval(() => {
        setResendCooldownSeconds((prev) => {
          if (prev <= 1) {
            if (resendCooldownIntervalRef.current) {
              clearInterval(resendCooldownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (resendCooldownIntervalRef.current) {
        clearInterval(resendCooldownIntervalRef.current);
      }
    };
  }, [resendCooldownSeconds, isEmailVerification]);

  // Resend email mutation
  const { mutate: resendEmail, isPending: resendPending } = useMutation({
    ...resendEmailVerificationWithdrawalRequestMutation(),
    onSuccess: () => {
      setResendCooldownSeconds(RESEND_TIMEOUT_SECONDS);
    },
  });

  const {
    mfaToken,
    isPending: mfaPending,
    requestMfaChallenge,
    verifyOtpCode,
  } = useWithdrawalOtpMfa();
  const { verifyOtpWithdrawal, isPending: otpVerifyPending } =
    useOtpVerifyWithdrawal();

  const handleResend = useCallback(() => {
    if (resendCooldownSeconds <= 0 && verificationData) {
      resendEmail({
        path: {
          withdrawalRequestID: withdrawalRequestId,
        },
      } as ResendEmailVerificationWithdrawalRequestData);
    }
  }, [
    resendCooldownSeconds,
    resendEmail,
    verificationData,
    withdrawalRequestId,
  ]);

  const handleCancel = useCallback(() => {
    if (!verificationData) return;

    navigate(
      `../${ERoutes.Completion}/${withdrawalRequestId}?verification=${verificationQuery}&status=cancelled&accountId=${selectedAccount?.id}`,
      {
        state: {
          money: verificationData.transactionData?.total_money,
          balance: verificationData.selectedAccount.balance,
          status: EnumCashierTxStatus.CashierTxCancelled,
          external_trading_account_id: verificationData.selectedAccount.id,
        },
      }
    );
  }, [
    navigate,
    withdrawalRequestId,
    verificationQuery,
    selectedAccount?.id,
    verificationData,
  ]);

  useEffect(() => {
    if (!isOtpVerification || hasRequestedMfaRef.current || mfaToken) {
      return;
    }

    hasRequestedMfaRef.current = true;
    requestMfaChallenge().catch(() => {
      setOtpError(t("withdrawals.verification.error.try_again"));
    });
  }, [isOtpVerification, mfaToken, requestMfaChallenge, t]);

  const handleOtpVerify = useCallback(async () => {
    if (!verificationData || !withdrawalRequestId) {
      return;
    }

    setOtpError(null);

    if (!otpCode || otpCode.length < 6 || !/^\d+$/.test(otpCode)) {
      setOtpError(t("withdrawals.verification.error.enter_valid_otp"));
      return;
    }

    if (!mfaToken) {
      setOtpError(t("withdrawals.verification.error.try_again"));
      return;
    }

    try {
      const mfaAccessToken = await verifyOtpCode(otpCode);

      const result = await verifyOtpWithdrawal({
        withdrawalRequestId,
        accessToken: mfaAccessToken,
      });

      const isCancelled =
        result.confirmation_status ===
        EnumConfirmationStatus.EnumConfirmationStatusCancelled;
      const completionState = {
        money: result.trading_money,
        balance: verificationData.selectedAccount.balance,
        status: isCancelled
          ? EnumCashierTxStatus.CashierTxCancelled
          : EnumCashierTxStatus.CashierTxComplete,
        external_trading_account_id: verificationData.selectedAccount.id,
      };

      navigate(
        `../${ERoutes.Completion}/${withdrawalRequestId}?status=${isCancelled ? "cancelled" : "approved"}&verification=otp&accountId=${selectedAccount?.id}`,
        {
          state: completionState,
        }
      );
    } catch {
      setOtpError(t("withdrawals.verification.error.try_again"));
    }
  }, [
    verificationData,
    withdrawalRequestId,
    mfaToken,
    otpCode,
    verifyOtpCode,
    verifyOtpWithdrawal,
    navigate,
    t,
    selectedAccount?.id,
  ]);

  if (!verificationData || !withdrawalRequestId) {
    logger.error("Missing withdrawal verification data or request ID", {
      event: "withdrawal_verification_invalid",
      withdrawalRequestId,
      hasVerificationData: !!verificationData,
    });
    return <Navigate to={`../${ERoutes.ErrorPage}`} />;
  }

  if (!isEmailVerification && !isOtpVerification) {
    logger.error("Unsupported withdrawal verification type", {
      event: "withdrawal_verification_invalid",
      verificationQuery,
    });
    return <Navigate to={`../${ERoutes.ErrorPage}`} />;
  }

  const isPrimaryPending = resendPending || mfaPending || otpVerifyPending;

  return (
    <GridPageLayout>
      <Section>
        <TransactionStatus
          account={verificationData.selectedAccount}
          transactionType="withdrawal"
          transactionStatus={{
            money: verificationData.transactionData?.total_money,
            status: "Pending",
            balance: verificationData.selectedAccount.balance,
            external_trading_account_id:
              verificationData.selectedAccount?.id ?? "",
          }}
          timerProps={{
            "data-testid": "withdrawal-verification-timer",
            timerSeconds: initialTimerSeconds,
            createdAt: createdAt ?? "",
            totalSeconds: (timeLimitInMinutes ?? 60) * 60,
            onTimerEnd: () => {
              navigate(
                `../${ERoutes.Completion}/${withdrawalRequestId}?status=cancelled&verification=${verificationQuery}&timeout=true&accountId=${selectedAccount?.id}`,
                {
                  state: {
                    money: verificationData.transactionData?.total_money,
                    balance: verificationData.selectedAccount.balance,
                    status: EnumCashierTxStatus.CashierTxCancelled,
                    external_trading_account_id:
                      verificationData.selectedAccount.id,
                  },
                }
              );
            },
          }}
          otpProps={
            isOtpVerification
              ? {
                  value: otpCode,
                  onChange: (value) => {
                    setOtpCode(value);
                    if (otpError) {
                      setOtpError(null);
                    }
                  },
                  error: otpError,
                }
              : undefined
          }
        />

        <NavButtons
          backLabel={t("deposit.methods.saved_methods.cancel")}
          backAction={handleCancel}
          backDisabled={isPrimaryPending}
          nextLabel={
            isOtpVerification
              ? t("withdrawals.verification.verify")
              : t("withdrawals.verification.resend_email")
          }
          nextAction={isOtpVerification ? handleOtpVerify : handleResend}
          nextDisabled={
            isOtpVerification
              ? isPrimaryPending
              : resendCooldownSeconds > 0 || isPrimaryPending
          }
        />
      </Section>
    </GridPageLayout>
  );
};
